// WhatsApp-style message ticks, shared by Chat.jsx and ChatModal.jsx.
//   sending   -> clock        (not saved yet)
//   sent      -> one grey tick (saved on the server)
//   delivered -> two grey ticks (reached every other member's app/browser)
//   read      -> two blue ticks (every other member opened the chat while looking at it)
// The server keeps who received / read each message in `delivered_to` / `is_read`.
// In a one-to-one chat "every other member" is just the other person.

const idList = (v) => (Array.isArray(v) ? v.map(String) : []);

// A deleted account never receives or reads anything, so it must not hold a message on grey.
// The server returns a bare { id } (no name/email) when the user record no longer exists.
function isGoneMember(m) {
  if (!m || typeof m !== 'object') return false;
  if (m.is_deleted === true || m.status === 0 || m.status === '0') return true;
  return !m.first_name && !m.last_name && !m.email;
}

function memberIds(room) {
  return (room?.members || [])
    .filter((m) => !isGoneMember(m))
    .map((m) => (m && typeof m === 'object' ? (m.id ?? m._id) : m))
    .filter((id) => id !== undefined && id !== null)
    .map(String);
}

export function receiptStatus(msg, room) {
  if (msg.isFailed) return 'failed';
  if (msg.isSending) return 'sending';
  if (msg.is_read === true) return 'read'; // legacy boolean flag
  const sender = String(msg.sender_id);
  const read = new Set(idList(msg.is_read).filter((id) => id !== sender));
  const delivered = new Set([...idList(msg.delivered_to), ...read].filter((id) => id !== sender));
  const others = memberIds(room).filter((id) => id !== sender);
  if (others.length) {
    if (others.every((id) => read.has(id))) return 'read';
    if (others.every((id) => delivered.has(id))) return 'delivered';
    return 'sent';
  }
  // Members not known: fall back to "anyone"
  if (read.size) return 'read';
  if (delivered.size || msg.isDelivered) return 'delivered';
  return 'sent';
}

function addTo(msg, field, userId) {
  const list = Array.isArray(msg[field]) ? msg[field] : [];
  if (list.map(String).includes(String(userId))) return msg;
  const n = Number(userId);
  return { ...msg, [field]: [...list, Number.isNaN(n) ? userId : n] };
}

// `messageDelivered` { messageId, userId, clientId }: one message reached one user.
// clientId matches a message that is still showing under its temporary id.
export function applyDeliveredAck(messages, data) {
  const ids = [data.messageId, data.clientId].filter(Boolean).map(String);
  return messages.map((m) => {
    const keys = [m._id, m.id, m._stableKey].filter(Boolean).map(String);
    if (!keys.some((k) => ids.includes(k))) return m;
    if (data.userId === undefined || data.userId === null) return { ...m, isDelivered: true };
    return addTo(m, 'delivered_to', data.userId);
  });
}

// `messagesDelivered` / `messagesRead` { user_id, up_to }: all of MY messages sent up to
// `up_to` reached / were read by user_id. Events about my own reads are ignored.
function applyOne(m, receipt) {
  let n = addTo(m, 'delivered_to', receipt.user_id);
  if (receipt.kind === 'read') n = addTo(n, 'is_read', receipt.user_id);
  return n;
}

export function applyBulkReceipt(messages, data, kind, myId) {
  if (!data || String(data.user_id) === String(myId)) return messages;
  const limit = data.up_to ? new Date(data.up_to).getTime() : Infinity;
  const receipt = { user_id: data.user_id, up_to: data.up_to || null, kind };
  return messages.map((m) => {
    if (String(m.sender_id) !== String(myId) || m.isFailed) return m;
    // Still waiting for the send response: its server time is unknown yet, so park the receipt
    // and let mergeReceipts apply it once the saved message arrives.
    if (m.isSending) return { ...m, _earlyReceipts: [...(m._earlyReceipts || []), receipt] };
    const t = new Date(m.createdAt || 0).getTime();
    if (t && t > limit) return m;
    return applyOne(m, receipt);
  });
}

// When the saved message replaces its optimistic copy, keep receipts that already arrived.
export function mergeReceipts(tempMsg, savedMsg) {
  const union = (a, b) => [...new Set([...(Array.isArray(a) ? a : []), ...(Array.isArray(b) ? b : [])].map(String))]
    .map((x) => (Number.isNaN(Number(x)) ? x : Number(x)));
  let merged = {
    ...savedMsg,
    delivered_to: union(tempMsg?.delivered_to, savedMsg?.delivered_to),
    is_read: union(tempMsg?.is_read, savedMsg?.is_read),
  };
  const t = new Date(savedMsg?.createdAt || 0).getTime();
  (tempMsg?._earlyReceipts || []).forEach((r) => {
    const limit = r.up_to ? new Date(r.up_to).getTime() : Infinity;
    if (!t || t <= limit) merged = applyOne(merged, r);
  });
  return merged;
}

// Messages count as read only while the person is actually looking at the page:
// tab visible AND window focused (WhatsApp Web behaviour).
export function isUserLookingAtPage() {
  if (typeof document === 'undefined') return true;
  const visible = document.visibilityState === 'visible';
  const focused = typeof document.hasFocus === 'function' ? document.hasFocus() : true;
  return visible && focused;
}
